import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SinavModal from "./SinavModal";
import axios from "axios";
import "../styles/EgitimStep.css";

const EgitimStep = ({ egitim, onClose }) => {
  const navigate = useNavigate();

  const [aktifAdim, setAktifAdim] = useState(0);             // 0-based index
  const [baslangicSaniyesi, setBaslangicSaniyesi] = useState(0);
  const [videoIzlendi, setVideoIzlendi] = useState(false);
  const [showSinav, setShowSinav] = useState(false);
  const [baslaButonYazi, setBaslaButonYazi] = useState(egitim?.hasExam === false ? "Eğitimi Tamamla" : "Sınava Başla");
  const [ilkYuklemeTamam, setIlkYuklemeTamam] = useState(false);

  const videoRef = useRef(null);
  const stepRefs = useRef([]);

  const moduller = Array.isArray(egitim?.moduller) ? egitim.moduller : [];
  const aktifModul = moduller[aktifAdim];

  // --- API: mevcut ilerlemeyi getir ---
  const kontrolEt = useCallback(async () => {
    const token = localStorage.getItem("token");
    const modulId = egitim?.id || 1;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/Isg/getir?modulId=${modulId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data) {
        const nextIndex = Math.max(
          0,
          Math.min((res.data.videoId ? res.data.videoId - 1 : 0), Math.max(0, moduller.length - 1))
        );
        setAktifAdim(nextIndex);
        setBaslangicSaniyesi(res.data.saniye || 0);
        setBaslaButonYazi(res.data.basariDurumu ? "Tamamlandı" : (egitim?.hasExam === false ? "Eğitimi Tamamla" : "Sınava Başla"));
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setBaslaButonYazi(egitim?.hasExam === false ? "Eğitimi Tamamla" : "Sınava Başla");
        try {
          await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/Isg/ekle?modulId=${modulId}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (postErr) {
          console.error("Kayıt oluşturulamadı:", postErr);
        }
      } else {
        console.error("Kontrol Et hatası:", err);
      }
    }
  }, [egitim?.id, moduller.length, egitim?.hasExam]);

  // --- API: saniye güncelle ---
  const videoTakip = useCallback(async () => {
    const token = localStorage.getItem("token");
    const modul = egitim?.id || 1;
    const videoId = aktifAdim + 1;
    const saniye = Math.floor(videoRef.current?.currentTime || 0);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/Isg/guncelle`,
        { modul, videoId, saniye },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error("Takip hatası:", err);
    }
  }, [egitim?.id, aktifAdim]);

  // --- ilk yüklemede ilerlemeyi al ---
  useEffect(() => { kontrolEt(); }, [kontrolEt]);

  // --- modül sayısı değişirse aktif indeksi clamp’le ---
  useEffect(() => {
    setAktifAdim((prev) => {
      const maxIdx = Math.max(0, moduller.length - 1);
      return Math.min(Math.max(prev, 0), maxIdx);
    });
  }, [moduller.length]);

  // --- periyodik progress (yalnızca HTML5 video) ---
  useEffect(() => {
    const int = setInterval(() => {
      if (videoRef.current && !videoRef.current.paused) videoTakip();
    }, 5000);
    return () => clearInterval(int);
  }, [aktifAdim, videoTakip]);

  // --- step kutularına auto-scroll ---
  useEffect(() => {
    if (stepRefs.current[aktifAdim]) {
      stepRefs.current[aktifAdim].scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [aktifAdim]);

  // --- ileri sarma kısıtı (kayıtlı saniyenin ötesine atlamayı engelle) ---
  useEffect(() => {
    const int = setInterval(() => {
      if (videoRef.current) {
        videoRef.current.lastAllowedTime = videoRef.current.currentTime;
      }
    }, 10000);
    return () => clearInterval(int);
  }, []);

  const handleSeeking = useCallback(() => {
    if (!ilkYuklemeTamam) return;
    const video = videoRef.current;
    if (!video) return;

    const allowed = video.lastAllowedTime || 0;
    const current = video.currentTime;
    if (current > allowed + 1) {
      video.currentTime = allowed;
    }
  }, [ilkYuklemeTamam]);

  const handleDevam = useCallback(() => {
    if (aktifAdim < moduller.length - 1) {
      setAktifAdim((p) => p + 1);
      setVideoIzlendi(false);
      setBaslangicSaniyesi(0);
    }
  }, [aktifAdim, moduller.length]);

  const handleGeri = useCallback(() => {
    if (aktifAdim > 0) {
      setAktifAdim((p) => p - 1);
      setVideoIzlendi(false);
      setBaslangicSaniyesi(0);
    }
  }, [aktifAdim]);

  const handleSinavBasla = useCallback(async () => {
    try {
      await videoTakip();
    } catch { }

    if (egitim?.hasExam === false) {
      // Sınav yoksa direkt bitir
      handleSinavBitir(true);
    } else {
      setShowSinav(true);
    }
  }, [videoTakip, egitim?.hasExam]);

  const handleSinavBitir = useCallback(
    (basarili) => {
      setShowSinav(false);
      if (basarili) {
        setBaslaButonYazi("Tamamlandı");
        onClose();
        navigate("/profil", { state: { modulTamamlandi: egitim.id } });
      } else {
        setBaslaButonYazi("Sınavı Tekrarla");
      }
    },
    [egitim?.id, navigate, onClose]
  );

  return (
    <div className="egitim-step-overlay">
      <div className="egitim-step-container">
        <button className="egitim-step-close" onClick={onClose}>✖</button>

        <div className="egitim-step-bar">
          {moduller.map((modul, index) => (
            <div
              key={index}
              ref={(el) => (stepRefs.current[index] = el)}
              className={`step-box ${index === aktifAdim ? "aktif" : index < aktifAdim ? "tamamlandi" : ""}`}
            >
              {modul.ad}
            </div>
          ))}
        </div>

        <div className="egitim-step-video">
          <video
            ref={videoRef}
            width="100%"
            controls
            preload="auto"
            muted
            playsInline
            webkit-playsinline="true"
            onEnded={() => setVideoIzlendi(true)}
            onLoadedMetadata={() => {
              const hedef = Number(baslangicSaniyesi) || 0;
              const video = videoRef.current;
              if (video && hedef > 0) {
                let tries = 0;
                const iv = setInterval(() => {
                  if (video.readyState >= 1) {
                    video.currentTime = hedef;
                    clearInterval(iv);
                    setIlkYuklemeTamam(true);
                  }
                  tries++;
                  if (tries > 20) {
                    clearInterval(iv);
                    setIlkYuklemeTamam(true);
                  }
                }, 200);
              } else {
                setIlkYuklemeTamam(true);
              }
            }}
            onSeeking={handleSeeking}
            key={aktifModul?.video || `m-${aktifAdim}`}
          >
            <source src={aktifModul?.video} type="video/mp4" />
          </video>
        </div>

        <div className="egitim-step-buttons three-column">
          {aktifAdim > 0 ? (
            <button className="geri-btn" onClick={handleGeri}>Geri Dön</button>
          ) : (
            <div style={{ width: "100px" }} />
          )}

          {aktifAdim === moduller.length - 1 ? (
            <button
              className="sinav-btn center"
              onClick={handleSinavBasla}
              disabled={!videoIzlendi || baslaButonYazi === "Tamamlandı"}
            >
              {baslaButonYazi}
            </button>
          ) : (
            <div style={{ width: "100px" }} />
          )}

          {aktifAdim < moduller.length - 1 ? (
            <button className="devam-btn" onClick={handleDevam} disabled={!videoIzlendi}>Devam Et</button>
          ) : (
            <div style={{ width: "100px" }} />
          )}
        </div>
      </div>

      {showSinav && (
        <SinavModal
          onKapat={() => setShowSinav(false)}
          onBitir={handleSinavBitir}
          aktifModul={egitim.id}
          kartId={egitim?.id}
        />
      )}
    </div>
  );
};

export default EgitimStep;
